import { useState, useEffect, useRef } from 'react';
import { 
  linkedListJava,
  linkedListPython,
  linkedListTraversalPython,
  linkedListTraversalJava,
  linkedListTraversalCpp,
  linkedListTraversalC,
  linkedListCpp,
  linkedListC
} from './linkedListAlgorithms';
import defaultExplanations from './explanations';
import codeSamplesTemplate from './codeSamples';
import { useSpeechSynthesis } from '../speechUtils';

export const useLinkedListTraversal = (speakingLang, translatedExplanations, data, language, breakpoints) => {
  const [explanations, setExplanations] = useState(defaultExplanations);
  const [highlightedLine, setHighlightedLine] = useState(null);
  const [focusedEndLine, setFocusedEndLine] = useState(null);
  const [callingLine, setCallingLine] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const [delay, setDelay] = useState(2000);
  const delayRef = useRef(delay);
  const breakpointsRef = useRef(breakpoints);
  const [logs, setLogs] = useState([]);
  const [variables, setVariables] = useState({});
  const [nodeVariables, setNodeVariables] = useState({});
  const [headVariables, setHeadVariables] = useState({});
  const [highlightedHeadVariables, setHighlightedHeadVariables] = useState([]);
  const [highlightedVariables, setHighlightedVariables] = useState([]);
  const [highlightedNodeVariables, setHighlightedNodeVariables] = useState([]);
  const [highlightDataPart, setHighlightDataPart] = useState([]);
  const [highlightNextAddressPart, setHighlightNextAddressPart] = useState([]);
  const [highlightNodeAddress, setHighlightNodeAddress] = useState([]);
  const [codeSamples, setCodeSamples] = useState(codeSamplesTemplate[language]);
  const { speak, setIsMuted } = useSpeechSynthesis(isPausedRef, speakingLang);
  const [isMuted, setLocalIsMuted] = useState(false);
  const isMutedRef = useRef(isMuted);
  const hasRun = useRef(false);
  const [globalList, setGlobalList] = useState(null);


  useEffect(() => {
    setIsMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    breakpointsRef.current = breakpoints;
  }, [breakpoints]);

  useEffect(() => {
    if (translatedExplanations && language) {
      setExplanations((prevExplanations) => ({
        ...prevExplanations,
        [language]: Object.keys(prevExplanations[language] || {}).reduce((acc, key, index) => {
          acc[key] = translatedExplanations[index] || prevExplanations[language][key];
          return acc;
        }, {}),
      }));
    } else {
      setExplanations(defaultExplanations);
    }
  }, [translatedExplanations, language]);

  const updateVariables = () => {
    resetState();
    createLinkedListVisual(language);
  };

  const createLinkedListVisual = (language) => {
    if (language === 'Python') {
      createLinkedListPython();
    } else if (language === 'Java') {
      createLinkedListJava();
    } else if (language === 'C') {
      createLinkedListC();
    } else if (language === 'C++') {
      createLinkedListCpp();
    }
  }

  useEffect(() => {
    if (!hasRun.current) {
      createLinkedListVisual(language);
      hasRun.current = true;
    }
  }, []);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  const resetState = () => {
    setHighlightedLine(null);
    setFocusedEndLine(null);
    setCallingLine(null);
    setVariables({});
    setNodeVariables({});
    setHeadVariables({});
    setLogs([]);
    setHighlightedVariables([]);
    setHighlightedNodeVariables([]);
    setHighlightedHeadVariables([]);
    setHighlightDataPart([]);
    setHighlightNextAddressPart([]);
    setHighlightNodeAddress([]);
  };

  const logMessage = async (message) => {
    setLogs((prevLogs) => [...prevLogs, message]);
    await speak(message);
  };

  const highlightLine = async (lineNumber, customLog=null) => {
    setHighlightedLine(lineNumber);
    
    const logMessageText = customLog 
    ? `Line ${lineNumber + 1}: ${customLog}` 
    : `Line ${lineNumber + 1}: ${explanations[language][lineNumber + 1] || "Executing line " + (lineNumber + 1)}`;

    await logMessage(logMessageText);
  };

  const highlightCallingLine = async (lineNumber) => {
    setCallingLine(lineNumber);
  };

  const focusEndLine = async (lineNumber) => {
    setFocusedEndLine(lineNumber);
    let message = "End of the execution";
    await speak(message);
  };

  const customSleep = async (lineNumber = null, time = null) => {
    // Check if the current line matches a breakpoint
    if (lineNumber !== null && breakpointsRef.current.includes(lineNumber)) {
      setIsPaused(true); // Pause execution
      isPausedRef.current = true;

      // Wait until `isPaused` is set to false
      await new Promise((resolve) => {
          const interval = setInterval(() => {
              if (!isPausedRef.current) {
                  clearInterval(interval);
                  resolve();
              }
          }, 100);
      });

      return;
    }

    // Handle regular sleep with pause checks
    let start = Date.now();
    let ms;
    if (time === null) {
        ms = delayRef.current;
    } else {
        ms = time;
    }
    
    while (Date.now() - start < ms) {
      if (isPausedRef.current) {
        await new Promise((resolve) => {
          let interval = setInterval(() => {
            if (!isPausedRef.current) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        });
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  };

  const createLinkedListPython = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    setHeadVariables((vars) => ({ ...vars, 
      head: { variable_name: 'head', value: 'None' }
    }));

    const list = await linkedListPython(
      data,
      setNodeVariables,
      setHeadVariables
    );

    setGlobalList(list);

    setIsRunning(false);
  };

  const runLinkedListTraversalPython = async () => {
    setFocusedEndLine(null);
    setLogs([]);
    setIsRunning(true);
    setIsPaused(false);

    await linkedListTraversalPython(
      globalList,
      highlightCallingLine,
      highlightLine,
      customSleep,
      logMessage,
      setHeadVariables,
      setHighlightedHeadVariables,
      setHighlightNextAddressPart,
      setHighlightNodeAddress,
      setHighlightDataPart
    );

    await focusEndLine(22);
    setIsRunning(false);
  };

  const createLinkedListJava = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    setHeadVariables((vars) => ({ ...vars, 
      head: { variable_name: 'head', value: 'null' }
    }));

    const list = await linkedListJava(
      data,
      setNodeVariables,
      setHeadVariables
    );

    setGlobalList(list);

    setIsRunning(false);
  };

  const runLinkedListTraversalJava = async () => {
    setFocusedEndLine(null);
    setLogs([]);
    setIsRunning(true);
    setIsPaused(false);

    await linkedListTraversalJava(
      globalList,
      highlightCallingLine,
      highlightLine,
      customSleep,
      logMessage,
      setHeadVariables,
      setHighlightedHeadVariables,
      setHighlightNextAddressPart,
      setHighlightNodeAddress,
      setHighlightDataPart
    );

    await focusEndLine(31);
    setIsRunning(false);
  };

  const createLinkedListCpp = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    setHeadVariables((vars) => ({ ...vars, 
      head: { variable_name: 'head', value: 'nullptr' }
    }));

    const list = await linkedListCpp(
      data,
      setNodeVariables,
      setHeadVariables
    );

    setGlobalList(list);

    setIsRunning(false);
  };

  const runLinkedListTraversalCpp= async () => {
    setFocusedEndLine(null);
    setLogs([]);
    setIsRunning(true);
    setIsPaused(false);

    await linkedListTraversalCpp(
      globalList,
      highlightCallingLine,
      highlightLine,
      customSleep,
      logMessage,
      setHeadVariables,
      setHighlightedHeadVariables,
      setHighlightNextAddressPart,
      setHighlightNodeAddress,
      setHighlightDataPart
    );

    await focusEndLine(42);
    setIsRunning(false);
  };

  const createLinkedListC = async () => {
    resetState();
    setIsRunning(true);
    setIsPaused(false);

    setHeadVariables((vars) => ({ ...vars, 
      head: { variable_name: 'head', value: 'nullptr' }
    }));

    const list = await linkedListC(
      data,
      setNodeVariables,
      setHeadVariables
    );

    setGlobalList(list);

    setIsRunning(false);
  };

  const runLinkedListTraversalC= async () => {
    setFocusedEndLine(null);
    setLogs([]);
    setIsRunning(true);
    setIsPaused(false);

    await linkedListTraversalC(
      globalList,
      highlightCallingLine,
      highlightLine,
      customSleep,
      logMessage,
      setHeadVariables,
      setHighlightedHeadVariables,
      setHighlightNextAddressPart,
      setHighlightNodeAddress,
      setHighlightDataPart
    );

    await focusEndLine(27);
    setIsRunning(false);
  };


  const runLinkedList = async () => {
    if (language === 'Python') {
      await runLinkedListTraversalPython();
    } else if (language === 'Java') {
      await runLinkedListTraversalJava();
    } else if (language === 'C') {
      await runLinkedListTraversalC();
    } else if (language === 'C++') {
      await runLinkedListTraversalCpp();
    }
  };

  return {
    highlightedLine,
    focusedEndLine,
    callingLine,
    isRunning,
    isPaused,
    delay,
    logs,
    variables,
    nodeVariables,
    headVariables,
    highlightedVariables,
    highlightedNodeVariables,
    highlightedHeadVariables,
    highlightDataPart,
    highlightNextAddressPart,
    highlightNodeAddress,
    isMuted,
    codeSamples,
    speak,
    setIsMuted: setLocalIsMuted,
    updateVariables,
    setHighlightedLine,
    setFocusedEndLine,
    setCallingLine,
    setIsRunning,
    setIsPaused,
    setDelay,
    setLogs,
    setVariables,
    setNodeVariables,
    setHeadVariables,
    setHighlightedVariables,
    setHighlightedNodeVariables,
    setHighlightedHeadVariables,
    setHighlightDataPart,
    setHighlightNextAddressPart,
    setHighlightNodeAddress,
    runLinkedList
  };
};
